import React, { useCallback, useEffect, useRef, useState } from "react";
import { Container } from "reactstrap";
import Pusher from "pusher-js";
import { toast } from "react-toastify";

import {
  fetchConversations,
  fetchMessages,
  sendMessage,
} from "../../apis/MessagesApi";
import { useAuth } from "../../context/AuthContext";
import ConversationsColumn from "../../components/inbox/ConversationsColumn";
import MessagesColumn from "../../components/inbox/MessagesColumn";
import ReservationColumn from "../../components/inbox/ReservationColumn";

// ---------------------------------------------------------------------------
// Main InboxPage component
// ---------------------------------------------------------------------------
const InboxPage = () => {
  const [conversations, setConversations] = useState([]);
  const [conversationsFiltered, setConversationsFiltered] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reservation, setReservation] = useState(null);
  const [pusher, setPusher] = useState(null);
  const [channelName, setChannelName] = useState("");
  const [newMessageText, setNewMessageText] = useState("");
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [latestLoaded, setLatestLoaded] = useState(1);
  const [listCurrentPage, setListCurrentPage] = useState(1);
  const [scrollToBottom, setScrollToBottom] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [mobileSectionShown, setMobileSectionShown] = useState("conversations");

  const { user } = useAuth();

  const limit = 10;
  const listLimit = 10;

  const messageBoxRef = useRef(null);
  const messageContentRef = useRef(null);
  const startHeightRef = useRef(0);

  // -------------------------------------------------------------------------
  // Group messages by date
  // -------------------------------------------------------------------------
  const groupedMessages = React.useMemo(() => {
    const groups = {};
    for (const msg of messages) {
      const date = new Date(msg.updatedAt || msg.createdAt).toDateString();
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    }

    Object.values(groups).forEach((msgs) =>
      msgs.sort(
        (a, b) =>
          new Date(a.updatedAt || a.createdAt).getTime() -
          new Date(b.updatedAt || b.createdAt).getTime(),
      ),
    );

    return Object.fromEntries(
      Object.entries(groups).sort(
        (a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime(),
      ),
    );
  }, [messages]);

  // -------------------------------------------------------------------------
  // Scroll to bottom when new messages arrive
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (scrollToBottom && messageBoxRef.current) {
      messageBoxRef.current.scrollTop = messageBoxRef.current.scrollHeight;
    } else if (
      !scrollToBottom &&
      messageBoxRef.current &&
      messageContentRef.current
    ) {
      // After loading older messages, preserve scroll position
      const newHeight = messageContentRef.current.clientHeight;
      messageBoxRef.current.scrollTop = newHeight - startHeightRef.current;
    }
  }, [groupedMessages, scrollToBottom]);

  // -------------------------------------------------------------------------
  // Load older messages on scroll to top
  // -------------------------------------------------------------------------
  const handleMessageScroll = useCallback(() => {
    if (!messageBoxRef.current) return;
    if (messageBoxRef.current.scrollTop === 0) {
      if (
        selectedConversation &&
        Number(totalPages) > Number(currentPage) &&
        Number(currentPage) + 1 !== Number(latestLoaded)
      ) {
        startHeightRef.current = messageContentRef.current?.clientHeight || 0;
        setScrollToBottom(false);
        loadMessagesPage(selectedConversation._id, currentPage + 1);
      }
    }
  }, [totalPages, currentPage, latestLoaded, selectedConversation]);

  useEffect(() => {
    const el = messageBoxRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleMessageScroll);
    return () => el.removeEventListener("scroll", handleMessageScroll);
  }, [handleMessageScroll]);

  // -------------------------------------------------------------------------
  // Load conversations list scroll (load more)
  // -------------------------------------------------------------------------
  const convListRef = useRef(null);
  const loadMoreConversations = useCallback(async () => {
    if (!convListRef.current) return;
    const el = convListRef.current;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      const nextPage = listCurrentPage + 1;
      try {
        const result = await fetchConversations({
          page: nextPage,
          limit: listLimit,
        });
        if (result?.conversations?.length) {
          setConversations((prev) => [...prev, ...result.conversations]);
          setListCurrentPage(nextPage);
        }
      } catch (err) {
        console.error("Load more conversations failed:", err);
      }
    }
  }, [listCurrentPage, listLimit]);

  useEffect(() => {
    const el = convListRef.current;
    if (!el) return;
    el.addEventListener("scroll", loadMoreConversations);
    return () => el.removeEventListener("scroll", loadMoreConversations);
  }, [loadMoreConversations]);

  // -------------------------------------------------------------------------
  // Filter conversations by keyword
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!keyword) {
      setConversationsFiltered(conversations);
      return;
    }
    const kw = keyword.toLowerCase();
    setConversationsFiltered(
      conversations.filter((conv) => {
        const other = conv.participants?.find(
          (p) => p._id?.toString() !== user?._id?.toString(),
        );
        if (!other) return false;
        return `${other.firstName || ""} ${other.lastName || ""}`
          .toLowerCase()
          .includes(kw);
      }),
    );
  }, [keyword, conversations, user]);

  // -------------------------------------------------------------------------
  // Load messages for a conversation (paginated)
  // -------------------------------------------------------------------------
  const loadMessagesPage = async (conversationId, page) => {
    try {
      const result = await fetchMessages({
        conversationId,
        currentPage: page,
        limit,
      });
      const fetched = result?.messages || [];
      setTotalPages(result?.totalPages || 0);
      setCurrentPage(page);
      setLatestLoaded(page);
      // prepend older messages
      setMessages((prev) => [...fetched.reverse(), ...prev]);
    } catch (err) {
      console.error("Failed to load messages:", err);
    }
  };

  // -------------------------------------------------------------------------
  // Select conversation
  // -------------------------------------------------------------------------
  const handleSelectConversation = useCallback(
    async (conversation) => {
      setSelectedConversation(conversation);
      setMobileSectionShown("messages");
      setMessages([]);
      setCurrentPage(1);
      setLatestLoaded(1);
      setScrollToBottom(true);
      setReservation(conversation.reservation || null);
      setLoadingMessages(true);

      try {
        const result = await fetchMessages({
          conversationId: conversation._id,
          currentPage: 1,
          limit,
        });
        const fetched = result?.messages || [];
        setTotalPages(result?.totalPages || 0);
        setMessages(fetched.reverse());
      } catch (err) {
        console.error("Failed to load messages:", err);
      } finally {
        setLoadingMessages(false);
      }
    },
    [limit],
  );

  // -------------------------------------------------------------------------
  // Initialize Pusher
  // -------------------------------------------------------------------------
  const initPusher = useCallback((token) => {
    try {
      const appKey = process.env.REACT_APP_PUSHER_APP_KEY;
      if (!appKey) return;

      const headers = {
        Authorization: `Bearer ${token}`,
        theme: localStorage.getItem("theme") || "light",
        language: localStorage.getItem("language") || "en",
        currency: localStorage.getItem("currency") || "usd",
        "x-api-secret": process.env.REACT_APP_API_SECRET || "",
      };

      const instance = new Pusher(appKey, {
        cluster: process.env.REACT_APP_PUSHER_CLUSTER || "eu",
        channelAuthorization: {
          endpoint: `${process.env.REACT_APP_BASE_URL}/api/chat/auth`,
          headers,
          transport: "ajax",
        },
      });
      setPusher(instance);
      return instance;
    } catch (err) {
      console.error("Pusher init failed:", err);
    }
  }, []);

  // -------------------------------------------------------------------------
  // Subscribe / unsubscribe to Pusher channel on conversation change
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!pusher || !selectedConversation) return;

    // Unsubscribe from old channel
    if (channelName) pusher.unsubscribe(channelName);

    const newChannelName = selectedConversation.channelName;
    setChannelName(newChannelName);

    const channel = pusher.subscribe(newChannelName);
    channel.bind("new-message", (data) => {
      // Deduplicate: if we already have this message (added by handleSend), skip it
      setMessages((prev) => {
        if (
          data._id &&
          prev.some((m) => m._id?.toString() === data._id?.toString())
        ) {
          return prev;
        }
        const msg = {
          _id: data._id || `temp-${Date.now()}`,
          content: data.content,
          updatedAt: data.timestamp || new Date(),
          createdAt: data.timestamp || new Date(),
          sender: data.sender,
          conversation: selectedConversation._id,
        };
        setScrollToBottom(true);
        return [...prev, msg];
      });
      // Update last message in conversation list
      setConversations((prev) =>
        prev.map((c) =>
          c._id === selectedConversation._id
            ? { ...c, lastMessage: { content: data.content } }
            : c,
        ),
      );
    });

    return () => {
      channel.unbind("new-message");
      pusher.unsubscribe(newChannelName);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pusher, selectedConversation]);

  // -------------------------------------------------------------------------
  // Initialize page
  // -------------------------------------------------------------------------
  useEffect(() => {
    const init = async () => {
      setLoadingList(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        // Initialize Pusher
        initPusher(token);

        // Fetch conversations
        const result = await fetchConversations({ page: 1, limit: listLimit });
        const convs = result?.conversations || [];
        setConversations(convs);
        setConversationsFiltered(convs);

        // Auto-select first
        if (convs.length > 0) {
          await handleSelectConversation(convs[0]);
        }
      } catch (err) {
        console.error("Inbox init error:", err);
      } finally {
        setLoadingList(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------------------------
  // Send message
  // -------------------------------------------------------------------------
  const handleSend = async () => {
    const trimmed = newMessageText.trim();
    if (!trimmed || !selectedConversation || sending) return;

    setSending(true);
    try {
      const result = await sendMessage({
        conversationId: selectedConversation._id,
        content: trimmed,
      });

      const newMsg = result?.message;
      if (newMsg) {
        setScrollToBottom(true);
        setMessages((prev) => {
          // If Pusher already delivered this message before the API response returned, skip it
          if (
            newMsg._id &&
            prev.some((m) => m._id?.toString() === newMsg._id?.toString())
          ) {
            return prev;
          }
          return [...prev, newMsg];
        });
        setConversations((prev) =>
          prev.map((c) =>
            c._id === selectedConversation._id
              ? { ...c, lastMessage: { content: trimmed } }
              : c,
          ),
        );
      }
      setNewMessageText("");
    } catch (err) {
      console.error("Send message failed:", err);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="page-content dark:bg-darkBg1 dark:text-white">
      <Container fluid>
        <div className="mb-3 md:mb-4">
          <h4 className="font-bold text-base md:text-xl dark:text-white">
            Support Inbox
          </h4>
          <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
            View and reply to user support conversations
          </p>
        </div>

        {loadingList ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500 dark:text-gray-400">
              Loading conversations...
            </div>
          </div>
        ) : (
          <div
            className="flex border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            style={{ height: "calc(100vh - 180px)", minHeight: 400 }}
          >
            <ConversationsColumn
              mobileSectionShown={mobileSectionShown}
              keyword={keyword}
              setKeyword={setKeyword}
              conversationsFiltered={conversationsFiltered}
              selectedConversation={selectedConversation}
              currentUserId={user?._id}
              onSelect={handleSelectConversation}
              convListRef={convListRef}
            />

            <MessagesColumn
              mobileSectionShown={mobileSectionShown}
              setMobileSectionShown={setMobileSectionShown}
              selectedConversation={selectedConversation}
              currentUserId={user?._id}
              loadingMessages={loadingMessages}
              groupedMessages={groupedMessages}
              messageBoxRef={messageBoxRef}
              messageContentRef={messageContentRef}
              newMessageText={newMessageText}
              setNewMessageText={setNewMessageText}
              sending={sending}
              handleSend={handleSend}
              handleKeyDown={handleKeyDown}
            />

            <ReservationColumn
              mobileSectionShown={mobileSectionShown}
              setMobileSectionShown={setMobileSectionShown}
              reservation={reservation}
            />
          </div>
        )}
      </Container>
    </div>
  );
};

export default InboxPage;
